/**
 * Governance Module - Unified Theme
 * Consistent visual style aligned with GlobalSidebar
 * Version: 1.0 (30 Jan 2026)
 */

// Color Palette (aligned with GlobalSidebar)
export const COLORS = {
  primary: '#111111',       // Brand green
  gold: '#b48c2c',          // Brand gold accent
  bg: '#f8fafc',            // Page background (slate-50)
  bgLight: '#f9fafb',       // Light background variation
  white: '#ffffff',
  text: '#1f2937',          // Text primary (gray-900)
  textLight: '#6b7280',     // Text secondary (gray-500)
  textMuted: '#9ca3af',     // Text tertiary (gray-400)
  border: '#e5e7eb',        // Border default (gray-200)
  borderLight: '#f3f4f6',   // Border light (gray-100)
  success: '#059669',       // Emerald-600
  warning: '#d97706',       // Amber-600
  error: '#dc2626',         // Red-600
  info: '#2563eb'           // Blue-600
};

// Typography
export const TYPOGRAPHY = {
  pageTitle: 'text-3xl font-extrabold',
  sectionTitle: 'text-lg font-bold',
  cardTitle: 'text-base font-bold',
  label: 'text-sm font-medium',
  body: 'text-sm',
  small: 'text-xs',
  tiny: 'text-[10px] font-bold uppercase tracking-wider'
};

// Spacing (Tailwind utilities)
export const SPACING = {
  pageContainer: 'max-w-7xl mx-auto px-4 lg:px-6 py-6',
  cardPadding: 'p-6',
  sectionGap: 'space-y-6',
  gridGap: 'gap-6'
};

// Component Styles (Tailwind class strings)
export const STYLES = {
  // Cards
  card: 'bg-white rounded-xl border border-gray-200 shadow-sm',
  cardHeader: 'px-6 py-4 border-b border-gray-100',
  cardBody: 'p-6',
  cardFooter: 'px-6 py-4 border-t border-gray-100',
  
  // Panels
  panel: 'bg-white rounded-xl border border-gray-200 overflow-hidden',
  panelHeader: 'px-4 py-3 bg-gray-50 border-b border-gray-100',
  
  // Tables
  table: 'w-full',
  tableHeader: 'bg-gray-50 border-b border-gray-100',
  tableHeaderCell: 'px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase',
  tableRow: 'border-b border-gray-100 hover:bg-gray-50 transition-colors duration-150',
  tableCell: 'px-4 py-3 text-sm',
  
  // Forms
  input: 'w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 text-sm bg-gray-50/50',
  textarea: 'w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 text-sm',
  select: 'w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 text-sm bg-white',
  
  // Buttons
  buttonPrimary: 'px-4 py-2.5 rounded-lg font-bold text-white transition-all duration-200 hover:opacity-90',
  buttonSecondary: 'px-4 py-2.5 rounded-lg font-medium border border-gray-200 hover:bg-gray-50 transition-all duration-200',
  buttonGhost: 'px-4 py-2.5 rounded-lg font-medium hover:bg-gray-100 transition-all duration-200',
  buttonDanger: 'px-4 py-2.5 rounded-lg font-bold text-white bg-red-600 hover:bg-red-700 transition-all duration-200',
  
  // Tabs
  tabContainer: 'flex gap-1 border-b border-gray-200',
  tab: 'px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all duration-200',
  tabActive: 'border-b-2 border-gray-900 text-gray-900 bg-gray-50',
  tabInactive: 'text-gray-600 hover:text-gray-900 hover:bg-gray-50',
  
  // Badges
  badge: 'inline-flex items-center px-2 py-1 rounded text-xs font-bold',
  badgeSuccess: 'bg-gray-100 text-gray-800',
  badgeWarning: 'bg-amber-100 text-amber-800',
  badgeError: 'bg-red-100 text-red-800',
  badgeInfo: 'bg-blue-100 text-blue-800',
  badgeNeutral: 'bg-gray-100 text-gray-800',
  
  // States
  skeleton: 'bg-gray-100 animate-pulse rounded',
  emptyState: 'text-center py-12 text-gray-500',
  divider: 'border-t border-gray-100'
};

// Agent Classification Badges
export const AGENT_CLASS_STYLES = {
  A: { bg: '#f3f4f6', text: '#111111', label: 'A - Critical' },
  B: { bg: '#fef3c7', text: '#92400e', label: 'B - Core' },
  C: { bg: '#dbeafe', text: '#1e40af', label: 'C - Support' },
  D: { bg: '#e5e7eb', text: '#374151', label: 'D - Backfill' }
};

// Priority Styles
export const PRIORITY_STYLES = {
  P0: { bg: '#fee2e2', text: '#991b1b', label: 'P0 - Critical' },
  P1: { bg: '#fed7aa', text: '#9a3412', label: 'P1 - High' },
  P2: { bg: '#fef3c7', text: '#92400e', label: 'P2 - Medium' },
  P3: { bg: '#dbeafe', text: '#1e40af', label: 'P3 - Low' }
};

// Status Styles
export const STATUS_STYLES = {
  active: { bg: '#f3f4f6', text: '#111111', label: 'Active' },
  pending: { bg: '#fef3c7', text: '#92400e', label: 'Pending' },
  blocked: { bg: '#fee2e2', text: '#991b1b', label: 'Blocked' },
  done: { bg: '#dbeafe', text: '#1e40af', label: 'Done' },
  disabled: { bg: '#e5e7eb', text: '#374151', label: 'Disabled' }
};

// Helper Functions
export const getBadgeStyle = (type, value) => {
  switch (type) {
    case 'agent_class':
      return AGENT_CLASS_STYLES[value] || AGENT_CLASS_STYLES.D;
    case 'priority':
      return PRIORITY_STYLES[value] || PRIORITY_STYLES.P3;
    case 'status':
      return STATUS_STYLES[value] || STATUS_STYLES.pending;
    default:
      return { bg: COLORS.border, text: COLORS.text };
  }
};

export const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

export const formatPercent = (value) => {
  return `${(value || 0).toFixed(1)}%`;
};

export const formatNumber = (value) => {
  return new Intl.NumberFormat('en-US').format(value || 0);
};

// Default Export
export default {
  COLORS,
  TYPOGRAPHY,
  SPACING,
  STYLES,
  AGENT_CLASS_STYLES,
  PRIORITY_STYLES,
  STATUS_STYLES,
  getBadgeStyle,
  formatCurrency,
  formatPercent,
  formatNumber
};
